import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import { useLogin } from '../../contexts/LoginContext';
import { useScrollFx, useReveal, useCountUp } from './useScrollFx';
import {
  checkInviteCode,
  joinWaitlist,
  setPendingInviteCode,
  getPendingInviteCode,
} from '../../services/access';
import './LandingPage.css';
import { MCP_URL, MCP_EXAMPLE_PROMPTS } from '../../config/mcp';
import { EXTENSION_URL, EXTENSION_NAME } from '../../config/extension';

const SHOT = (name) => `${process.env.PUBLIC_URL || ''}/landing/${name}`;

// Requests are reviewed by hand; this is the floor for "said something useful".
const MIN_REASON_CHARS = 25;

const SEO_TITLE = 'QuantApe | The stock screener that helps you build strategies and test them';
// The one-line positioning: the hero lede says the same thing in the same
// words, so a search snippet and the page it lands on make one claim (#276).
const SEO_DESCRIPTION =
  'Other screeners let you filter on attributes. QuantApe goes further — it connects the dots, ' +
  'looks behind the curtain and highlights the developments that help anticipate stock movements. ' +
  'Backtested swing strategies, live signal watches, sector rotation maps and AI-built screens ' +
  'across 8,780 tickers. By invitation.';
const CANONICAL_URL = 'https://quantape.com/';

/**
 * Meta for the landing page.
 *
 * ListSEO lives inside <App/>, which never mounts for an anonymous visitor —
 * and an anonymous visitor is what every crawler is. Without this the page
 * falls back to index.html's static tags, which still describe the old open
 * screener.
 *
 * Since the invitation gate serves this same page on every route, only "/"
 * carries unique content; every other path is told not to index itself, or
 * ~200 sitemap URLs would compete as duplicates of each other.
 */
const LandingSEO = () => {
  const { pathname } = useLocation();
  const isHome = pathname === '/';

  return (
    <Helmet>
      <title>{SEO_TITLE}</title>
      <meta name="description" content={SEO_DESCRIPTION} />
      <link rel="canonical" href={CANONICAL_URL} />
      {!isHome && <meta name="robots" content="noindex,follow" />}
      <meta property="og:title" content={SEO_TITLE} />
      <meta property="og:description" content={SEO_DESCRIPTION} />
      <meta property="og:url" content={CANONICAL_URL} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={SEO_TITLE} />
      <meta name="twitter:description" content={SEO_DESCRIPTION} />
    </Helmet>
  );
};

/* ------------------------------------------------------------------ *
 * Building blocks
 * ------------------------------------------------------------------ */

/** Wraps content in a reveal-on-scroll transition. `delay` staggers siblings. */
const Reveal = ({ children, delay = 0, as: Tag = 'div', className = '', ...rest }) => {
  const [ref, shown] = useReveal();
  return (
    <Tag
      ref={ref}
      className={`lp-reveal ${shown ? 'is-in' : ''} ${className}`.trim()}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
};

/**
 * A product screenshot presented as a floating pane. `depth` sets how far it
 * drifts against the scroll — the whole page's sense of dimension comes from
 * neighbouring panes being given different depths.
 */
const Shot = ({ src, alt, width, height, depth = 1, className = '', frame = true, priority = false }) => (
  <div
    className={`lp-shot ${frame ? 'is-framed' : ''} ${className}`.trim()}
    data-fx=""
    style={{ '--depth': depth }}
  >
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      // Above-the-fold panes load eagerly: they ARE the hero, so deferring them
      // just pushes out the largest contentful paint.
      loading={priority ? 'eager' : 'lazy'}
      fetchpriority={priority ? 'high' : undefined}
      decoding="async"
    />
  </div>
);

/**
 * The alternating narrative row used by most of the page. One component rather
 * than eight near-identical blocks, so spacing and motion stay identical
 * everywhere and only the content differs.
 */
const Feature = ({ eyebrow, title, body, points, shot, flip = false, tone = 'plain', id, children }) => (
  <section className={`lp-feature ${flip ? 'is-flipped' : ''} lp-tone-${tone}`} id={id}>
    <div className="lp-feature-inner">
      <div className="lp-feature-copy">
        <Reveal>
          <p className="lp-eyebrow">{eyebrow}</p>
          <h2 className="lp-h2">{title}</h2>
          <p className="lp-body">{body}</p>
          {points?.length > 0 && (
            <ul className="lp-points">
              {points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
          {children}
        </Reveal>
      </div>
      <Reveal className="lp-feature-visual" delay={90}>
        {shot}
      </Reveal>
    </div>
  </section>
);

/**
 * Heading block that opens each half of the page (issue #283): the tools that
 * help you NOTICE something, then the tools that TEST it. Same measure and
 * type as a feature's copy column, so it reads as a chapter title rather than
 * a new design element.
 */
const PartIntro = ({ id, number, eyebrow, title, body }) => (
  <section className="lp-part" id={id}>
    <Reveal className="lp-part-inner">
      <p className="lp-eyebrow">
        <span className="lp-part-number">{number}</span>
        {eyebrow}
      </p>
      <h2 className="lp-h2">{title}</h2>
      <p className="lp-body lp-body-wide">{body}</p>
    </Reveal>
  </section>
);

/* ------------------------------------------------------------------ *
 * Invitation form
 * ------------------------------------------------------------------ */

const InviteForm = ({ id, autoFocus = false }) => {
  const { triggerLogin } = useLogin();
  const [code, setCode] = useState(() => getPendingInviteCode());
  const [state, setState] = useState('idle'); // idle | checking | valid | error
  const [message, setMessage] = useState('');
  const [mode, setMode] = useState('code'); // code | waitlist | waitlisted
  const [waitEmail, setWaitEmail] = useState('');
  const [waitName, setWaitName] = useState('');
  const [waitReason, setWaitReason] = useState('');
  const [waitBusy, setWaitBusy] = useState(false);

  const submitCode = useCallback(
    async (event) => {
      event.preventDefault();
      if (!code.trim() || state === 'checking') return;

      setState('checking');
      setMessage('');
      try {
        const result = await checkInviteCode(code);
        if (!result?.valid) {
          setState('error');
          setMessage(result?.message || "That invitation code isn't valid.");
          return;
        }
        // Park the verified code so it survives the full-page Google redirect,
        // then hand off to sign-in. App.js redeems it once the session exists.
        setPendingInviteCode(result.code);
        setState('valid');
        setMessage('Invitation accepted — continue with Google to finish.');
        triggerLogin();
      } catch {
        setState('error');
        setMessage('Could not reach the server. Try again in a moment.');
      }
    },
    [code, state, triggerLogin]
  );

  const submitWaitlist = useCallback(
    async (event) => {
      event.preventDefault();
      if (waitBusy) return;

      // We read every request by hand, so a blank "why" makes the queue
      // impossible to triage — hold the submit until there's something to read.
      if (waitReason.trim().length < MIN_REASON_CHARS) {
        setState('error');
        setMessage(
          `Tell us a little more — at least a few words about what you'd use QuantApe for (${waitReason.trim().length}/${MIN_REASON_CHARS} characters).`
        );
        return;
      }

      setWaitBusy(true);
      try {
        const result = await joinWaitlist({ email: waitEmail, name: waitName, reason: waitReason });
        if (result.success) {
          setMode('waitlisted');
        } else {
          setMessage(result.message || 'Enter a valid email address.');
          setState('error');
        }
      } catch {
        setMessage('Could not reach the server. Try again in a moment.');
        setState('error');
      } finally {
        setWaitBusy(false);
      }
    },
    [waitEmail, waitName, waitReason, waitBusy]
  );

  if (mode === 'waitlisted') {
    return (
      <div className="lp-invite lp-invite-done" id={id}>
        <div className="lp-invite-done-mark" aria-hidden="true">✓</div>
        <h3>You&rsquo;re on the list.</h3>
        <p>
          We review requests in batches. If it&rsquo;s a fit, an invitation lands in your inbox with a
          code that unlocks this page.
        </p>
      </div>
    );
  }

  if (mode === 'waitlist') {
    return (
      <form className="lp-invite" id={id} onSubmit={submitWaitlist}>
        <label className="lp-field-label" htmlFor={`${id}-email`}>
          Request an invitation
        </label>
        <input
          id={`${id}-email`}
          className="lp-input"
          type="email"
          required
          placeholder="you@example.com"
          value={waitEmail}
          onChange={(e) => setWaitEmail(e.target.value)}
          autoComplete="email"
        />
        <input
          className="lp-input"
          type="text"
          placeholder="Your name (optional)"
          value={waitName}
          onChange={(e) => setWaitName(e.target.value)}
          autoComplete="name"
        />
        <label className="lp-field-label lp-field-label-inline" htmlFor={`${id}-reason`}>
          What draws you to QuantApe?
        </label>
        <textarea
          id={`${id}-reason`}
          className={`lp-input lp-textarea ${
            state === 'error' && waitReason.trim().length < MIN_REASON_CHARS ? 'is-error' : ''
          }`}
          rows={3}
          required
          minLength={MIN_REASON_CHARS}
          placeholder="A few words on what you'd actually use it for — the band strategies, the sector maps, earnings positioning, building your own screens…"
          value={waitReason}
          onChange={(e) => {
            setWaitReason(e.target.value);
            if (state === 'error') setState('idle');
          }}
        />
        <p className="lp-hint">
          {waitReason.trim().length >= MIN_REASON_CHARS
            ? 'Thanks — that helps.'
            : `Specifics get read first. ${waitReason.trim().length}/${MIN_REASON_CHARS} characters.`}
        </p>
        {state === 'error' && message && <p className="lp-msg is-error">{message}</p>}
        <div className="lp-invite-actions">
          <button className="lp-btn lp-btn-primary" type="submit" disabled={waitBusy}>
            {waitBusy ? 'Sending…' : 'Request invitation'}
          </button>
          <button
            className="lp-btn lp-btn-ghost"
            type="button"
            onClick={() => {
              setMode('code');
              setState('idle');
              setMessage('');
            }}
          >
            I have a code
          </button>
        </div>
      </form>
    );
  }

  return (
    <form className="lp-invite" id={id} onSubmit={submitCode}>
      <label className="lp-field-label" htmlFor={`${id}-code`}>
        Invitation code
      </label>
      <div className="lp-code-row">
        <input
          id={`${id}-code`}
          className={`lp-input lp-code-input ${state === 'error' ? 'is-error' : ''}`}
          type="text"
          placeholder="APE-XXXX-XXXX"
          value={code}
          spellCheck="false"
          autoCapitalize="characters"
          autoComplete="off"
          autoFocus={autoFocus}
          onChange={(e) => {
            setCode(e.target.value);
            if (state === 'error') setState('idle');
          }}
        />
        <button className="lp-btn lp-btn-primary" type="submit" disabled={state === 'checking'}>
          {state === 'checking' ? 'Checking…' : 'Enter'}
        </button>
      </div>
      {message && (
        <p className={`lp-msg ${state === 'error' ? 'is-error' : 'is-ok'}`} role="status">
          {message}
        </p>
      )}
      <button className="lp-link-btn" type="button" onClick={() => setMode('waitlist')}>
        No code? Request an invitation →
      </button>
    </form>
  );
};

/* ------------------------------------------------------------------ *
 * Page sections
 * ------------------------------------------------------------------ */

const LIST_NAMES = [
  'Reddit Trending', 'Day Gainers', 'Golden Cross', 'Oversold Reversal', 'Meme Stocks',
  'Politician-Owned', 'Recent IPOs', 'Most Shorted', 'Earnings Surprises', 'Analyst Favorites',
  'Support Bounce', 'Momentum Breakout', 'Near 52-Week High', 'Traffic Gainers', 'AI Beneficiaries',
  'Double Bottom', 'Index Inclusion Candidates', 'Small Cap Gainers', 'Cramer Picks',
  'Resistance Rejection', 'Aggressive Small Caps', 'Strategic Acquisition Targets',
];

const Nav = () => {
  const { triggerLogin } = useLogin();
  const [stuck, setStuck] = useState(false);

  useEffect(() => {
    const onScroll = () => setStuck(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`lp-nav ${stuck ? 'is-stuck' : ''}`}>
      <div className="lp-nav-inner">
        <a className="lp-brand" href="#top">
          <img src={`${process.env.PUBLIC_URL || ''}/quantape.png`} alt="" width="32" height="32" />
          <span className="lp-brand-text">
            <strong>QuantApe</strong>
            <em>Signals behind tomorrow&rsquo;s market moves</em>
          </span>
        </a>
        <nav className="lp-nav-links">
          <a href="#build">Build a strategy</a>
          <a href="#test">Test it</a>
        </nav>
        <div className="lp-nav-cta">
          <button className="lp-btn lp-btn-ghost" type="button" onClick={triggerLogin}>
            Sign in
          </button>
          <a className="lp-btn lp-btn-primary lp-btn-sm" href="#invite">
            Enter code
          </a>
        </div>
      </div>
    </header>
  );
};

const Hero = () => (
  <section className="lp-hero" id="top">
    <div className="lp-hero-bg" aria-hidden="true">
      <div className="lp-orb lp-orb-a" data-fx="" style={{ '--depth': 2.2 }} />
      <div className="lp-orb lp-orb-b" data-fx="" style={{ '--depth': -1.6 }} />
      <div className="lp-grid-tex" />
    </div>

    <div className="lp-hero-inner">
      <div className="lp-hero-copy">
        <p className="lp-eyebrow lp-hero-eyebrow">
          <span className="lp-dot" /> By invitation only
        </p>
        <h1 className="lp-h1">
          The stock screener that helps you <em>build strategies&nbsp;&mdash; and test them.</em>
        </h1>
        <p className="lp-lede">
          Other screeners let you filter on attributes. QuantApe goes further — it{' '}
          <strong>connects the dots</strong>, <strong>looks behind the curtain</strong>, and
          highlights the developments that help you anticipate where a stock is heading.
        </p>

        <InviteForm id="hero-invite" />

        <p className="lp-hero-note">
          Already inside? Use <strong>Sign in</strong> above — your invitation is remembered.
        </p>
      </div>

      <div className="lp-hero-stage" aria-hidden="true">
        <Shot
          src={SHOT('bollinger-autofit.png')}
          alt=""
          width={1110}
          height={464}
          depth={0.6}
          className="lp-stage-main"
          priority
        />
        <Shot
          src={SHOT('signal-watches.png')}
          alt=""
          width={500}
          height={585}
          depth={2.1}
          className="lp-stage-side"
          priority
        />
        <Shot
          src={SHOT('market-assessment.png')}
          alt=""
          width={916}
          height={413}
          depth={-1.2}
          className="lp-stage-under"
          priority
        />
      </div>
    </div>
  </section>
);

// Counted in tenths so the tween can land on a decimal (2.8M) with integer math.
const formatMillions = (v) => `${(v / 10).toFixed(1)}M`;

const StatBand = () => {
  const [tickersRef, tickersValue] = useCountUp(8780);
  const [listsRef, listsValue] = useCountUp(199);
  const [barsRef, barsValue] = useCountUp(28, { format: formatMillions });

  return (
    <section className="lp-stats">
      <div className="lp-stats-inner">
        <div className="lp-stat" ref={tickersRef}>
          <span className="lp-stat-value" ref={tickersValue}>8,780</span>
          <span className="lp-stat-label">tickers priced and scored, every session</span>
        </div>
        <div className="lp-stat" ref={listsRef}>
          <span className="lp-stat-value" ref={listsValue}>199</span>
          <span className="lp-stat-label">curated lists, rebuilt nightly</span>
        </div>
        <div className="lp-stat" ref={barsRef}>
          <span className="lp-stat-value" ref={barsValue}>2.8M</span>
          <span className="lp-stat-label">daily bars behind every backtest</span>
        </div>
      </div>
    </section>
  );
};

const Marquee = () => (
  <section className="lp-marquee" aria-hidden="true">
    <div className="lp-marquee-track">
      {[0, 1].map((copy) => (
        <div className="lp-marquee-group" key={copy}>
          {LIST_NAMES.map((name) => (
            <span className="lp-chip" key={`${copy}-${name}`}>
              {name}
            </span>
          ))}
        </div>
      ))}
    </div>
    <div className="lp-marquee-fade lp-marquee-fade-l" />
    <div className="lp-marquee-fade lp-marquee-fade-r" />
  </section>
);

/** Full-bleed, sticky-scale treatment for the two market maps. */
const Maps = () => {
  const ref = useRef(null);

  return (
    <section className="lp-maps" id="maps" ref={ref}>
      <div className="lp-maps-head">
        <Reveal>
          <p className="lp-eyebrow">Market maps</p>
          <h2 className="lp-h2">
            Where the money actually <em>went</em>.
          </h2>
          <p className="lp-body lp-body-wide">
            Every sector and industry, sized by how many companies sit inside it and coloured by the
            move — today, this week, this month, this quarter. Then the same market cut a second way,
            by <strong>business model</strong>: subscription against licensing, marketplace against
            retail, spread against transaction. Rotations that look invisible in a sector view are
            obvious here. And because every sector is scored every session, the map has a memory:{' '}
            <strong>sector sentiment over time</strong>, so you can see whether today&rsquo;s leader has
            been leading for a week or a quarter, and which sectors are quietly turning.
          </p>
        </Reveal>
      </div>

      <div className="lp-maps-stage">
        <Reveal className="lp-map-pane">
          <Shot src={SHOT('sector-map.png')} alt="Sector and industry heat map" width={1369} height={530} depth={0.5} />
          <p className="lp-caption">Sectors → industries, one quarter of price action.</p>
        </Reveal>
        <Reveal className="lp-map-pane" delay={120}>
          <Shot
            src={SHOT('business-models.png')}
            alt="The same market grouped by business model"
            width={1360}
            height={460}
            depth={-0.5}
          />
          <p className="lp-caption">The same market, grouped by how the companies make money.</p>
        </Reveal>
        <Reveal className="lp-map-pane" delay={120}>
          <Shot
            src={SHOT('sector-sentiment-history.png')}
            alt="Each sector's fear and greed score charted over time, with three sectors pinned"
            width={1369}
            height={378}
            depth={0.5}
          />
          <p className="lp-caption">
            Sector sentiment over time: a fear &amp; greed score per sector, every session. Pin the ones you
            care about; the rest stay in the background.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

const InviteSection = () => (
  <section className="lp-cta" id="invite">
    <div className="lp-cta-bg" aria-hidden="true">
      <div className="lp-orb lp-orb-c" data-fx="" style={{ '--depth': 1.4 }} />
    </div>
    <Reveal className="lp-cta-inner">
      <p className="lp-eyebrow">The experiment</p>
      <h2 className="lp-h2">
        Quant-grade signals, <em>in retail hands.</em>
      </h2>
      <p className="lp-body">
        The tools on this page used to require a desk, a data budget and a team to run them.
        QuantApe is an experiment in whether they hold up in the hands of an individual investor —
        backtested honestly, reported with their losses attached, and useful without a Bloomberg
        terminal. That question is the whole point.
      </p>
      <p className="lp-body">
        Which is why it opens slowly: a small number of people at a time, so the signal stays useful
        and the roadmap keeps following the people actually using it. If you have a code, use it. If
        you don&rsquo;t, tell us how you invest and we&rsquo;ll be in touch.
      </p>
      <div className="lp-cta-form">
        <InviteForm id="cta-invite" />
      </div>
    </Reveal>
  </section>
);

const LandingFooter = () => (
  <footer className="lp-footer">
    <div className="lp-footer-inner">
      <div className="lp-footer-brand">
        <img src={`${process.env.PUBLIC_URL || ''}/quantape.png`} alt="" width="28" height="28" />
        <div>
          <strong>QuantApe</strong>
          <p>Because insight isn&rsquo;t optional. It&rsquo;s everything.</p>
        </div>
      </div>
      <nav className="lp-footer-links">
        <Link to="/terms">Terms</Link>
        <Link to="/privacy">Privacy</Link>
        <Link to="/cookies">Cookies</Link>
        <a href="mailto:admin@quantape.com">Contact</a>
      </nav>
    </div>
    <div className="lp-footer-legal">
      <p>
        © {new Date().getFullYear()} DI SW VENTURES LLC. QuantApe is a research tool, not an
        investment adviser. Nothing here is a recommendation to buy or sell any security. Backtested
        results are historical and do not predict future returns.
      </p>
    </div>
  </footer>
);

/* ------------------------------------------------------------------ *
 * Page
 * ------------------------------------------------------------------ */

const LandingPage = ({ notice }) => {
  useScrollFx();

  // Opt this page out of the app's desktop `body { zoom: .9 }` rule.
  useEffect(() => {
    document.body.classList.add('lp-mode');
    return () => document.body.classList.remove('lp-mode');
  }, []);

  // A rejected sign-in bounces back here; surface why before anything else.
  const noticeRef = useRef(null);
  useEffect(() => {
    if (notice && noticeRef.current) {
      noticeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [notice]);

  const year = useMemo(() => new Date().getFullYear(), []);

  return (
    <div className="lp" data-year={year}>
      <LandingSEO />
      <Nav />

      {notice && (
        <div className="lp-notice" ref={noticeRef} role="alert">
          <strong>Not so fast.</strong> {notice}
        </div>
      )}

      <main>
        <Hero />
        <StatBand />
        <Marquee />

        <PartIntro
          id="build"
          number="01"
          eyebrow="Build a strategy"
          title={<>Notice it <em>before it is obvious.</em></>}
          body="Six different ways of seeing what the market is doing under the surface — the raw material a strategy is built from."
        />

        <Maps />

        <Feature
          eyebrow="Lists"
          title={<>199 ways in — or <em>describe your own.</em></>}
          body="Curated lists rebuild every night: momentum breakouts, oversold reversals, double bottoms, unusual short interest, politician holdings, index-inclusion candidates, companies whose web traffic is quietly inflecting. When none of them is the question you're asking, type the question instead and QuantApe builds the list."
          points={[
            '“Find technology companies whose revenue growth is accelerating. Mid market.”',
            'Every list carries its own move, so you can see which ideas are working',
            'Star the ones you use and they follow you across sessions',
            'Talk to ChatGPT or Claude about any list, your watchlist or today’s market — QuantApe is an MCP server, free daily allowance then pay per call',
          ]}
          id="research"
          shot={
            <div className="lp-shot-pair">
              <Shot
                src={SHOT('smart-lists.png')}
                alt="Curated smart lists with daily moves"
                width={411}
                height={985}
                depth={0.7}
                className="lp-shot-tall"
              />
              <Shot
                src={SHOT('ai-list.png')}
                alt="Generating a custom list with AI"
                width={381}
                height={392}
                depth={2.0}
                className="lp-shot-float"
              />
            </div>
          }
        />

        <Feature
          eyebrow="The daily read"
          title={<>A close that <em>explains itself.</em></>}
          body="Every session ends with a written read on what moved and what it implies — breadth, the cycle phase, sector leadership, the curve, and the headlines that mattered. Underneath it sits the sentiment picture: momentum, volatility, breadth, safe-haven demand and put/call, tracked as a series rather than a number that appears from nowhere. Then it splits eleven ways, because one number for the whole market tells you very little when energy is euphoric and real estate is not."
          points={[
            'Breadth, phase and yield curve slope in one line',
            'Fear & Greed broken into the seven inputs that produce it',
            'Sentiment scored per sector, with the five components behind each score',
            'Delivered to your inbox if you want it there instead',
          ]}
          flip
          tone="tint"
          shot={
            <div className="lp-shot-stack">
              <Shot
                src={SHOT('market-assessment.png')}
                alt="Daily market assessment with breadth, fear and greed, outlook and bonds"
                width={916}
                height={413}
                depth={0.6}
              />
              <Shot
                src={SHOT('sector-sentiment.png')}
                alt="Fear and greed scored across all eleven sectors"
                width={1289}
                height={421}
                depth={1.8}
                className="lp-shot-offset"
              />
            </div>
          }
        />

        <Feature
          eyebrow="Earnings"
          title={<>Know which week is <em>going to be loud.</em></>}
          body="A thirty-day histogram of what's reporting, with the bellwether days flagged before you get to them. Open a day and you get the companies, the options positioning into the print, the signals going in, and the news that's already moving them — plus a calendar feed you can subscribe to from your own calendar app."
          points={[
            'Post-earnings expirations, not the front-month weekly that expires too early',
            'Before-open and after-close classified from the real report time',
            'An expectations bar on every name — how much good news is already in the price',
            'Yesterday’s reporters kept one click away',
          ]}
          shot={
            <Shot
              src={SHOT('earnings-calendar.png')}
              alt="Earnings calendar with 30-day histogram and reporting companies"
              width={679}
              height={766}
              depth={1.2}
              className="lp-shot-tall"
            />
          }
        />

        <Feature
          eyebrow="Earnings calls"
          flip
          tone="tint"
          title={<>What the call <em>actually said.</em></>}
          body="Companies explain the quarter on the call, not in the press release — the partnership that just closed, the product nobody is buying yet, the question management would not answer. We transcribe the call audio ourselves the moment it is published, roughly two hours after the call ends and well ahead of the written transcript, then pull out what changed."
          points={[
            'Developments and named partners — not a restatement of the income statement',
            'Growth rates by segment, and what management credits for driving them',
            'Threats to that growth, ranked by how much is actually at risk',
            'The analyst Q&A: who asked what, and where the answer dodged',
          ]}
          shot={
            <Shot
              src={SHOT('call-highlights.png')}
              alt="Earnings call highlights for NVIDIA: developments, products, growth rates, growth drivers and ranked threats"
              width={1367}
              height={818}
              depth={1.2}
            />
          }
        />

        <section className="lp-closers">
          <div className="lp-closers-head">
            <Reveal>
              <p className="lp-eyebrow">The table</p>
              <h2 className="lp-h2">
                Built around <em>your</em> question.
              </h2>
              <p className="lp-body lp-body-wide">
                Web traffic, job openings, revenue per employee, employee outlook, short interest,
                alpha, since-earnings drift, how much is priced in before a print — the columns that
                tell you something the price already knows, next to the ones it doesn&rsquo;t. Choose
                seven and the table is yours. Open a company and the research is already written.
              </p>
            </Reveal>
          </div>
          <div className="lp-closers-grid">
            <Reveal className="lp-closer">
              <Shot
                src={SHOT('smart-screen.png')}
                alt="Customisable screener table with economic events inline"
                width={1411}
                height={693}
                depth={0.5}
              />
              <p className="lp-caption">Economic prints land inline, in the sequence they happened.</p>
            </Reveal>
            <Reveal className="lp-closer" delay={90}>
              <Shot src={SHOT('custom-columns.png')} alt="Column customisation dialog" width={584} height={619} depth={1.5} />
              <p className="lp-caption">Twenty-nine metrics. Seven slots. Your call.</p>
            </Reveal>
            <Reveal className="lp-closer lp-closer-wide" delay={180}>
              <Shot src={SHOT('swot.png')} alt="Generated SWOT analysis for a company" width={1302} height={382} depth={0.8} />
              <p className="lp-caption">Per-company SWOT, generated from the filings and the tape.</p>
            </Reveal>
            <Reveal className="lp-closer" delay={240}>
              <Shot
                src={SHOT('technical-signals.png')}
                alt="Technical signal lists for swing traders"
                width={405}
                height={686}
                depth={1.2}
                className="lp-shot-tall"
              />
              <p className="lp-caption">Signals grouped the way a swing trader actually scans.</p>
            </Reveal>
            <Reveal className="lp-closer lp-closer-text" delay={300}>
              <p className="lp-eyebrow">AI assistants</p>
              <p className="lp-closer-text-code">{MCP_URL}</p>
              <ul className="lp-closer-text-prompts">
                {MCP_EXAMPLE_PROMPTS.slice(0, 2).map((prompt) => (
                  <li key={prompt}>“{prompt}”</li>
                ))}
              </ul>
              <p className="lp-caption">
                Works with any AI assistant that supports MCP — ChatGPT, Claude, Cursor, VS Code
                and more. Free daily allowance, then pay per call.
              </p>
              <Link to="/mcp" className="lp-caption-link">How it works →</Link>
            </Reveal>
            <Reveal className="lp-closer lp-closer-wide lp-closer-ext" delay={360}>
              <a
                className="lp-closer-ext-shot"
                href={EXTENSION_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Get the ${EXTENSION_NAME} Chrome extension`}
              >
                <Shot
                  src={SHOT('extension.png')}
                  alt="QuantApe Assistant Chrome extension showing a GameStop profile over a social feed"
                  width={1000}
                  height={625}
                  depth={0.6}
                />
              </a>
              <div className="lp-closer-ext-copy">
                <p className="lp-eyebrow">Browser extension</p>
                <h3 className="lp-h3">
                  The research, <em>where you already read.</em>
                </h3>
                <p className="lp-caption">
                  {EXTENSION_NAME} spots tickers on any page — news, X, Reddit, a filing — and opens
                  the company&rsquo;s profile in place: fundamentals, momentum, ratings, earnings and
                  the risk factors, without leaving the tab.
                </p>
                <a
                  className="lp-caption-link"
                  href={EXTENSION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get it free on the Chrome Web Store →
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        <PartIntro
          id="test"
          number="02"
          eyebrow="Test it"
          title={<>A hunch is not a position. <em>Make it earn one.</em></>}
          body="Backtest the idea on the exact stock, put it on watch, and let the record decide."
        />

        <Feature
          id="signals"
          eyebrow="Swing strategies"
          title={<>Every band, <em>backtested</em> before you trade it.</>}
          body="Pick a stock and QuantApe fits the strategy to it: long or short, exiting at the band or reverting to the mean, across five SMA lengths. It reports the record — trades taken, win rate, total and average return — over the exact window you're looking at, so a strategy has to earn its place on the chart."
          points={[
            'Auto-fit picks the shape and period with the strongest record for that stock',
            'Entries test the same band the chart draws — what you see is what triggered',
            'Mean-reversion exits carry a time stop, so a broken trade never pins capital',
          ]}
          shot={
            <Shot
              src={SHOT('bollinger-autofit.png')}
              alt="Trading range analysis with auto-fit band strategy and win rate"
              width={1110}
              height={464}
              depth={0.8}
            />
          }
        />

        <Feature
          eyebrow="Signal watches"
          title={<>Now let the strategy run — <em>and keep score.</em></>}
          body="Once a band strategy has earned its place, put the stock on watch. QuantApe re-checks it at every close, tells you the day it enters and the day it exits, and keeps the ledger: what is open and how it is doing, what is closed and how it went. Every alert reaches you by push, webhook, or a signed feed your own scripts can read."
          points={[
            'One decision a day, at the close — no intraday noise, no chasing',
            'Open positions show entry, target and live P&L; closed trades keep wins and losses alike',
            'A time stop closes anything that stops behaving, so a bad trade can never sit forever',
          ]}
          flip
          tone="tint"
          shot={
            <Shot
              src={SHOT('signal-watches.png')}
              alt="Signal watches panel with recent signals, open positions and closed trades"
              width={500}
              height={585}
              depth={1.4}
              className="lp-shot-tall"
            />
          }
        />

        <Feature
          eyebrow="AI portfolios"
          title={<>Strategies that <em>keep their own notes.</em></>}
          body="Run model portfolios against distinct mandates — value, income, growth, hypergrowth, short- mid- and long-term momentum, earnings prediction. Each one rebalances on its own schedule, scores its last set of decisions against what actually happened, and writes down what it learned before the next cycle reads it back."
          points={[
            'Every trade carries the reasoning that produced it',
            'Outcomes are scored, not quietly forgotten',
            'Publish a portfolio and let others follow it',
          ]}
          shot={
            <Shot
              src={SHOT('ai-portfolios.png')}
              alt="AI managed portfolios with per-strategy performance"
              width={485}
              height={343}
              depth={1.1}
            />
          }
        />

        <InviteSection />
      </main>

      <LandingFooter />
    </div>
  );
};

export default LandingPage;
